import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, LoadingSpinner, SvgIcon, UI } from '@gik/ui';
import noop from '@gik/core/utils/noop';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import {
  CalendarAnnouncementForm,
  ICalendarAnnouncementFormProps,
  ICalendarAnnouncementFormValues,
} from './CalendarAnnouncementForm';
import { ICalendarAnnouncement, ICalendarEvent } from '@gik/calendar/models/Calendar';
import { IUpdateAnnouncementPayload } from '@gik/api/calendar/calendarAnnouncements';
import { convertDateFromBackend } from '@gik/core/utils/l10n';
import { timeDataFormat } from '@gik/l10n';
import { timeToInt } from 'time-number';
import RRule, { rrulestr } from 'rrule';
import { calendarAnnouncementFormValuesToPayload } from '../EventForm/utils';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { deleteEvent, updateEvent } from '@gik/api/calendar/calendarEvents';
import { dateTimeFormat } from '@gik/core/utils/DateTimeUtils';
import { openIntercomWindow } from '@gik/core/utils/Intercom';
import { useTranslation } from 'react-i18next';
import { translationKeys as commonTranslationKeys } from '@gik/i18n/en/common';
import { translationKeys as eventTranslationKeys } from '@gik/calendar/components/EventForm/i18n/en';
import { translationKeys } from './i18n/en';
import { translationKeys as eventFormTranslationKeys } from '../EventForm/i18n/en';
import { calendarEventMutatedAnalytics } from '@gik/calendar/utils/CalendarAnalytics';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';

export type CalendarEventFormEditMode = 'instance' | 'series';

export interface ICalendarEditAnnouncementFormProps extends ICalendarAnnouncementFormProps {
  entry: ICalendarAnnouncement;
  event: ICalendarEvent;
  onSuccess?: () => void;
  onDelete?: () => void;
}

export function CalendarEditAnnouncementForm({
  buttonsPortal,
  onSuccess = noop,
  onDelete = noop,
  entry,
  event,
  className,
  ...otherProps
}: ICalendarEditAnnouncementFormProps): React.ReactElement {
  const bem = bemBlock('calendar-edit-announcement-form');
  const { t } = useTranslation();

  const { data: eventTypes } = useCalendarEventTypes();

  const [errorMessage, setErrorMessage] = React.useState<string>();
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const [editMode, setEditMode] = React.useState<CalendarEventFormEditMode>();
  // const [startDate, setStartDate] = React.useState<Moment>();
  // const [endDate, setEndDate] = React.useState<Moment>();

  const savingRef = React.useRef<boolean>(false);
  function setSaving(value: boolean) {
    savingRef.current = value;
  }

  const deletingRef = React.useRef<boolean>(false);
  function setDeleting(value: boolean) {
    deletingRef.current = value;
  }

  // React.useEffect(() => {
  //   setStartDate();
  //   setEndDate(entry.endsAt ? convertDateFromBackend(editMode === 'instance' ? event.endsAt : entry.endsAt) : null);
  // }, [event, entry, editMode]);

  // get selected eventType
  const eventType = eventTypes?.find(eventType => eventType.id === entry.typeId);

  // const announcementEventType = eventTypes.find(item => item.slug === 'announcement');

  let weekdays: boolean[];
  let repeat = -1;

  if (entry.repeat) {
    const rrule = rrulestr(entry.repeat.toString());
    // freq = rrule.options.freq;

    if (rrule.options.freq === RRule.WEEKLY && rrule.origOptions.byweekday) {
      // custom repeat
      repeat = 10;
      weekdays = [false, false, false, false, false, false, false];
      rrule.options.byweekday.map(weekday => {
        weekdays[weekday] = true;
      });
    } else if (rrule.options.freq === RRule.DAILY && rrule.origOptions.interval === 2) {
      // every other day
      repeat = 11;
    } else {
      // normal repeat
      repeat = rrule.options.freq;
    }
  }

  async function handleSubmit(values: ICalendarAnnouncementFormValues) {
    let response: boolean;

    const payload: IUpdateAnnouncementPayload = {
      ...calendarAnnouncementFormValuesToPayload({ ...values }),
      inkindRouteId,
      instanceDate: editMode === 'instance' ? event.startsAt : null,
      typeId: CalendarEventTypeIds.Announcement,
    };

    if (!payload.announcementTypeId) {
      throw new Error(`announcementTypeId is "${payload.announcementTypeId}"`);
    }

    setErrorMessage(null);
    setSaving(true);

    try {
      response = await updateEvent(entry.id, payload);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message);
      setSaving(false);
      return false;
    }

    if (response) {
      calendarEventMutatedAnalytics(
        'eventUpdated',
        entry.id,
        payload.allDay,
        payload.startsAt,
        payload.endsAt,
        payload.repeat,
        payload.description,
        payload.title,
        payload.typeId,
        eventType.name,
        undefined,
        {
          announcementTypeId: payload.announcementTypeId,
          sendEmailToPageFollowers: payload.sendEmailToPageFollowers,
        }
      );
    }

    setSaving(false);
    onSuccess();
    return response;
  }

  async function handleDelete() {
    let shouldDelete = false;

    const editModeName =
      editMode === 'series' ? t(eventFormTranslationKeys.seriesType) : t(translationKeys.announcement);

    const dialogTitle = t(eventTranslationKeys.deleteRequestConfirmTitle, { type: editModeName });

    shouldDelete = await UI.confirm(
      t(eventTranslationKeys.deleteRequestConfirm, { type: editModeName.toLocaleLowerCase() }),
      {
        title: dialogTitle,
        okButtonProps: { variant: 'danger' },
        okText: t(commonTranslationKeys.delete),
      }
    );

    let response: boolean;
    if (!shouldDelete) return;

    setDeleting(true);

    try {
      response = await deleteEvent(entry.id, {
        instanceDate: editMode === 'instance' ? event?.startsAt : undefined,
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message);
      setDeleting(false);
      return;
    }

    setDeleting(false);
    if (response === false) return;
    onDelete();
  }

  function handleOpenIntercom() {
    openIntercomWindow();
  }

  function updateEditMode(type: CalendarEventFormEditMode) {
    setEditMode(type);

    let dialogTitle = t(eventTranslationKeys.editDialogTitle);
    if (type === 'instance') dialogTitle = t(eventTranslationKeys.editInstance);
    if (type === 'series') dialogTitle = t(eventTranslationKeys.editSeries);
    // FIXME: better way to update the dialog title
    const titleEl = document.querySelector('.gik-calendar-edit-announcement .gik-modal-header__title');
    if (titleEl) titleEl.innerHTML = dialogTitle;
  }

  // choose editMode if this is a repeated event
  if (entry && entry.repeat && editMode === undefined) {
    return (
      <div className={bem('edit-choice', null, className)}>
        <div className="close-btn" onClick={close}></div>

        <Button onClick={() => updateEditMode('series')}>{t(eventTranslationKeys.editSeries)}</Button>
        <p>{t(translationKeys.editSeriesDescription)}</p>
        <hr />
        <Button onClick={() => updateEditMode('instance')}>{t(eventTranslationKeys.editInstance)}</Button>
        <p>{t(eventTranslationKeys.editInstanceDescription)}</p>
      </div>
    );
  }

  if (!eventTypes) return <LoadingSpinner center />;

  if (!eventType) {
    throw new Error(`EventType '${eventType}' not recognized`);
  }

  const startDate = convertDateFromBackend(editMode === 'instance' ? event.startsAt : entry.startsAt);
  const endDate = convertDateFromBackend(editMode === 'instance' ? event.endsAt : entry.endsAt);

  const instanceStartTime = startDate.isValid() ? startDate?.format(timeDataFormat) : null;
  const instanceEndTime = endDate.isValid()
    ? endDate?.format(timeDataFormat)
    : startDate.clone().add(1, 'hour').format(timeDataFormat);

  const initialValues: ICalendarAnnouncementFormValues = {
    title: entry.title,
    description: entry.description,
    announcementTypeId: entry.announcementTypeId,
    allDay: entry.allDay,
    sendEmailToPageFollowers: entry.sendEmailToPageFollowers || false,
    startDate: startDate?.format(dateTimeFormat),
    endDate: endDate.isValid() ? endDate?.format(dateTimeFormat) : startDate?.format(dateTimeFormat),
    startTime: instanceStartTime !== null && instanceStartTime !== undefined ? timeToInt(instanceStartTime) : null,
    endTime: instanceEndTime !== null && instanceEndTime !== undefined ? timeToInt(instanceEndTime) : null,
    repeat: editMode === 'instance' ? -1 : repeat,
    weekdays,
  };

  return (
    <div className={bem(null, null, className)}>
      <CalendarAnnouncementForm
        initialValues={{ ...initialValues }}
        buttonsPortal={buttonsPortal}
        errorMessage={errorMessage}
        buttons={({ submitting }, formId: string) => {
          const isSubmitting = submitting || savingRef.current || deletingRef.current;
          return (
            <div className={'gik-form__actions-new'}>
              <section>
                <Button
                  variant="danger"
                  onClick={() => handleDelete()}
                  loading={deletingRef.current}
                  disabled={isSubmitting}
                  hideLabelOnMobile
                  prepend={<SvgIcon name="Trash" />}
                >
                  {t(commonTranslationKeys.delete)}
                </Button>
                <Button variant={'default-light'} onClick={handleOpenIntercom} circle>
                  <SvgIcon name={'InterrogationRed'} set={'gik-icons'} />
                </Button>
              </section>
              <section>
                <Button type="submit" form={formId} loading={savingRef.current} disabled={isSubmitting}>
                  {t(commonTranslationKeys.save)}
                </Button>
              </section>
            </div>
          );
        }}
        {...otherProps}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
